/**
 * Record-centric column picker — choose columns from MULTIPLE forms (joined on
 * record_id) plus record metadata. Each repeatable form selected exposes its
 * per-form collapse strategy (wide/aggregate/pinned). Edits spec.columns and
 * spec.formStrategies.
 */
import { useState } from 'react'
import { Columns3, ChevronDown, ChevronLeft, Layers } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { cn } from '@/lib/utils/cn'
import { useFormCatalogQuery } from '../api/use-explorer'
import type { ExplorerGroup } from '../api/explorer-api'
import {
  META_COLUMNS, WIDE_DEFAULT_CAP, DEFAULT_RECORD_FORM_STRATEGY,
  type ViewColumn, type FormInstanceStrategy, type FormInstanceMode, type AggregateFn,
} from '../types/data-view-spec'

const RECORD_MODES: { value: FormInstanceMode; label: string }[] = [
  { value: 'aggregate', label: 'تجمیع' }, { value: 'wide', label: 'عرضی' }, { value: 'pinned', label: 'نمونهٔ مشخص' },
]
const AGG: { value: AggregateFn; label: string }[] = [
  { value: 'last', label: 'آخرین' }, { value: 'first', label: 'اولین' }, { value: 'count', label: 'تعداد' },
  { value: 'min', label: 'کمینه' }, { value: 'max', label: 'بیشینه' }, { value: 'avg', label: 'میانگین' },
]

interface RecordColumnPickerProps {
  groups: ExplorerGroup[]
  columns: ViewColumn[]
  formStrategies: Record<string, FormInstanceStrategy>
  onColumnsChange: (cols: ViewColumn[]) => void
  onStrategyChange: (formId: string, s: FormInstanceStrategy) => void
}

export function RecordColumnPicker({
  groups, columns, formStrategies, onColumnsChange, onStrategyChange,
}: RecordColumnPickerProps) {
  const hasMeta = (key: string) => columns.some((c) => c.source === 'record_meta' && c.key === key)
  const toggleMeta = (key: string, on: boolean) =>
    on
      ? onColumnsChange([...columns, { id: `m_${key}`, source: 'record_meta', key }])
      : onColumnsChange(columns.filter((c) => !(c.source === 'record_meta' && c.key === key)))

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Columns3 className="w-3.5 h-3.5" />ستون‌ها (چند فرم)
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 max-h-[75vh] overflow-y-auto p-3 space-y-3">
        <div>
          <p className="text-[11px] font-semibold text-muted-foreground/70 uppercase mb-1">اطلاعات رکورد</p>
          {META_COLUMNS.filter((m) => m.key !== 'instance_index' && m.key !== 'submission_status').map((m) => (
            <label key={m.key} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-muted cursor-pointer text-sm">
              <Checkbox checked={hasMeta(m.key)} onCheckedChange={(v) => toggleMeta(m.key, !!v)} />
              <span className="flex-1">{m.label}</span>
            </label>
          ))}
        </div>
        {groups.map((g) => (
          <div key={g.id}>
            <p className="text-[11px] font-semibold text-muted-foreground/70 uppercase mb-1 flex items-center gap-1">
              <Layers className="w-3 h-3" />{g.title}
            </p>
            {g.forms.map((f) => (
              <FormFieldGroup
                key={f.id}
                formId={f.id}
                formTitle={f.title}
                repeatable={f.instanceType === 'repeatable'}
                columns={columns}
                strategy={formStrategies[f.id] ?? DEFAULT_RECORD_FORM_STRATEGY}
                onColumnsChange={onColumnsChange}
                onStrategyChange={(s) => onStrategyChange(f.id, s)}
              />
            ))}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  )
}

function FormFieldGroup({
  formId, formTitle, repeatable, columns, strategy, onColumnsChange, onStrategyChange,
}: {
  formId: string
  formTitle: string
  repeatable: boolean
  columns: ViewColumn[]
  strategy: FormInstanceStrategy
  onColumnsChange: (cols: ViewColumn[]) => void
  onStrategyChange: (s: FormInstanceStrategy) => void
}) {
  const [open, setOpen] = useState(false)
  const catalogQ = useFormCatalogQuery(open ? formId : undefined)
  const cat = catalogQ.data?.catalog

  const selectedCount = columns.filter((c) => c.source === 'field' && c.formId === formId).length
  const has = (key: string) => columns.some((c) => c.source === 'field' && c.formId === formId && c.key === key)
  const toggle = (key: string, on: boolean) =>
    on
      ? onColumnsChange([...columns, { id: `f_${formId}_${key}`, source: 'field', key, formId }])
      : onColumnsChange(columns.filter((c) => !(c.source === 'field' && c.formId === formId && c.key === key)))

  return (
    <div className="mb-1">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-1.5 px-1 py-1 rounded text-sm hover:bg-muted"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronLeft className="w-3.5 h-3.5 text-muted-foreground" />}
        <span className="flex-1 text-start">{formTitle}</span>
        {selectedCount > 0 && <span className="text-[10px] text-primary">{selectedCount}</span>}
        {repeatable && <span className="text-[10px] text-muted-foreground/60">چندتایی</span>}
      </button>
      {open && (
        <div className="ms-4 ps-2 border-s border-border">
          {repeatable && selectedCount > 0 && (
            <div className="flex items-center gap-1.5 py-1.5">
              <Select value={strategy.mode} onValueChange={(v) => onStrategyChange({ ...strategy, mode: v as FormInstanceMode })}>
                <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                <SelectContent>{RECORD_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
              {strategy.mode === 'aggregate' && (
                <Select value={strategy.aggregate ?? 'last'} onValueChange={(v) => onStrategyChange({ ...strategy, aggregate: v as AggregateFn })}>
                  <SelectTrigger className="h-7 text-xs w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>{AGG.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {strategy.mode === 'wide' && (
                <Input type="number" min={1} max={50} className="h-7 w-16 text-xs"
                  value={strategy.cap ?? WIDE_DEFAULT_CAP}
                  onChange={(e) => onStrategyChange({ ...strategy, cap: Math.max(1, Number(e.target.value) || WIDE_DEFAULT_CAP) })} />
              )}
              {strategy.mode === 'pinned' && (
                <Input type="number" min={1} className="h-7 w-16 text-xs"
                  value={strategy.pinnedIndex ?? 1}
                  onChange={(e) => onStrategyChange({ ...strategy, pinnedIndex: Math.max(1, Number(e.target.value) || 1) })} />
              )}
            </div>
          )}
          {catalogQ.isLoading && <p className="text-xs text-muted-foreground py-1">در حال بارگذاری…</p>}
          {cat?.fields.map((fld) => (
            <label key={fld.fieldKey} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-muted cursor-pointer text-sm">
              <Checkbox checked={has(fld.fieldKey)} onCheckedChange={(v) => toggle(fld.fieldKey, !!v)} />
              <span className="flex-1 truncate">{fld.label}</span>
            </label>
          ))}
          {cat?.calculated.map((c) => (
            <label key={c.field_key} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-muted cursor-pointer text-sm">
              <Checkbox checked={has(c.field_key)} onCheckedChange={(v) => toggle(c.field_key, !!v)} />
              <span className="flex-1 truncate">{c.label.fa || c.field_key} ƒ</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}
